import React from 'react';
import { Container, ItalicText, NoCasesContainer, Title } from './index.sc';
import { enums, get } from '../../utils';
import { CustomUploadFileInfo } from '../../utils/upload-helpers';
import { PersonDataContext } from '../../contexts/person-data-context';
import DocumentGraphic from '../../images/document-in-cloud.png';
import DocumentTypeSelection from './doc-type-select';
import CaseSelection from './case-selection';
import FileSelection from './file-selection';
import VBClaimSelect from './vb-claim-select';
import successTick from '../../images/success-tick.svg';
import errorImage from '../../images/yellow-exclamation.svg';
import { Button } from '@progress/kendo-react-buttons';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLDClient } from '@launchdarkly/react-sdk';
import { queryKeys, useVBClaims } from '../../hooks/use-data';
import { awaitNewUpload } from '../../hooks/use-upload-polling';
import { IVBClaim } from '../../data-models';
import { log } from '../../utils/log';
import { pushAnalyticsDataLayer } from '../../utils/analytics/helpers';

interface UploadProps {
  keyId?: string;
  files?: CustomUploadFileInfo[];
  uploadTried?: boolean;
  selectedCase?: object;
  sortableFiles?: object[];
  docType?: string;
  success?: boolean;
  error?: boolean;
}

const UploadSuccess = () => {
  React.useEffect(() => {
    const propsDataGtmDl = {
      attribute1: 'upload a document',
      attribute2: 'undefined',
      attribute3: 'upload_document_success',
      attribute4: 'undefined',
      label: 'submit',
      category: 'button',
      action: 'click',
    };
    pushAnalyticsDataLayer(null, propsDataGtmDl, 'fileSelection');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div
        id="successScreen"
        style={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <img
          alt="success"
          src={successTick}
          style={{
            paddingBottom: '24px',
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          fontFamily: 'Ubuntu',
          fontSize: '26px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '32px',
          paddingBottom: '24px',
          textAlign: 'center',
        }}
      >
        Thank you for submitting your document(s)!
      </div>
      <div
        style={{
          textAlign: 'center',
          fontFamily: 'Open Sans',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '24px',
        }}
      >
        We have received your upload and your specialist will review. Please
        allow 5 business days for your document to be reviewed. Please note,
        some documents may not be visible on the Portal due to compliance
        restrictions. Any viewable document will be available within 5 business
        days from receipt. You will receive a letter, phone call, and/or Task
        once our review is complete.
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: '36px',
        }}
      >
        <Button
          style={{
            display: 'flex',
            justifyContent: 'center',
            borderRadius: '360px',
            backgroundColor: '#037CB7',
          }}
        >
          <Link
            to={enums.AppPaths.appRoot}
            style={{
              color: '#fff',
            }}
          >
            Back to Summary
          </Link>
        </Button>
      </div>
    </>
  );
};

const uploadError = (
  setError: any,
  setShowDropDowns: any,
  setDocType: any,
  setSelectedCaseNew: any,
  setSelectedLeaveReason: any,
) => (
  <>
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <img alt="error" src={errorImage} style={{ paddingBottom: '24px' }} />
    </div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        fontFamily: 'Ubuntu',
        fontSize: '26px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '32px',
        paddingBottom: '24px',
        textAlign: 'center',
      }}
    >
      Something went wrong
    </div>
    <div
      style={{
        textAlign: 'center',
        fontFamily: 'Open Sans',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '24px',
      }}
    >
      Please try to upload the documents again
    </div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        paddingTop: '36px',
      }}
    >
      <Button
        style={{
          display: 'flex',
          justifyContent: 'center',
          borderRadius: '360px',
          backgroundColor: '#fff',
          border: '1px solid #037CB7',
        }}
      >
        <Link
          to={enums.AppPaths.appRoot}
          style={{
            color: '#037CB7',
            fontFamily: 'Open Sans',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '24px',
          }}
          data-gtm-dl={JSON.stringify({
            attribute1: 'upload a document',
            attribute2: 'undefined',
            attribute3: 'upload_failed_cancel_click',
            attribute4: 'undefined',
            category: 'button',
            label: 'cancel',
          })}
        >
          Cancel
        </Link>
      </Button>
      <Button
        style={{
          display: 'flex',
          justifyContent: 'center',
          borderRadius: '360px',
          backgroundColor: '#037CB7',
          marginLeft: '16px',
          color: '#fff',
          fontFamily: 'Open Sans',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '24px',
        }}
        onClick={() => {
          setError(false);
          setShowDropDowns(true);
          setDocType('');
          setSelectedLeaveReason('');
          setSelectedCaseNew('');
        }}
        data-gtm-dl={JSON.stringify({
          attribute1: 'upload a document',
          attribute2: 'undefined',
          attribute3: 'upload_failed_retry_click',
          attribute4: 'undefined',
          category: 'button',
          label: 'retry',
        })}
      >
        Retry
      </Button>
    </div>
  </>
);

const NewUploadDocuments: (p: UploadProps, props: any) => React.ReactElement =
  () => {
    const { personData } = React.useContext(PersonDataContext);
    const queryClient = useQueryClient();
    const [docType, setDocType] = React.useState<string>('');
    const [selectedCaseNew, setSelectedCaseNew] = React.useState<string>('');
    const [selectedLeaveReason, setSelectedLeaveReason] =
      React.useState<string>('');
    const [success, setSuccess] = React.useState<boolean>(false);
    const [showDropDowns, setShowDropDowns] = React.useState<boolean>(true);
    const [error, setError] = React.useState<boolean>(false);
    const [selectedVbClaims, setSelectedVbClaims] = React.useState<IVBClaim[]>(
      [],
    );

    const ldClient = useLDClient();
    const vbUploadsEnabled =
      ldClient?.boolVariation('enable-vb-uploads', false) ?? false;

    // kicked off on page load so the list is cached before the user reaches it
    const { data: vbClaims, isLoading: vbClaimsLoading } =
      useVBClaims(vbUploadsEnabled);

    const claims = get(personData?.cases, 'claims', []);
    const leaves = get(personData?.cases, 'leaves', []);
    const accommodations = get(personData?.cases, 'accommodations', []);

    const handleSuccess = (value: boolean) => {
      setSuccess(value);
      if (!value) {
        return;
      }

      queryClient.refetchQueries({
        queryKey: [queryKeys.activities],
      });
      // the new upload's activity lands shortly after the upload returns;
      // once it's pulled, the upload poller starts its checkpoints
      awaitNewUpload(queryClient, selectedCaseNew);
    };

    log.debug(selectedLeaveReason);

    //Combine claims and leaves
    const formattedCases = claims.concat(leaves).concat(accommodations);

    const containerId = 'new-upload-container';

    const caseSelected =
      selectedCaseNew !== '' && selectedCaseNew !== 'Select a claim';
    const docTypeSelected = docType !== '' && docType !== undefined;
    const showVbClaimSelect =
      vbUploadsEnabled && caseSelected && docTypeSelected && showDropDowns;
    const availableVbClaims = vbUploadsEnabled ? vbClaims ?? [] : [];

    if (formattedCases.length === 0) {
      return (
        <Container id={containerId}>
          <Title>Upload Documents</Title>
          <NoCasesContainer>
            <img src={DocumentGraphic} alt="" width="179" height="148" />
            <ItalicText className="mt-4">
              {`You will be able to upload documents once you have an active claim or leave.`}
            </ItalicText>
          </NoCasesContainer>
        </Container>
      );
    }

    if (success === true && error === false) {
      return (
        <Container id={containerId}>
          <UploadSuccess />
        </Container>
      );
    } else if (error === true) {
      return (
        <Container id={containerId}>
          {uploadError(
            setError,
            setShowDropDowns,
            setDocType,
            setSelectedCaseNew,
            setSelectedLeaveReason,
          )}
        </Container>
      );
    } else {
      const renderUploadForm = () => (
        <>
          <Title>Upload Documents</Title>
          {showDropDowns === true ? (
            <CaseSelection
              setSelectedCaseNew={setSelectedCaseNew}
              setSelectedLeaveReason={setSelectedLeaveReason}
            />
          ) : null}
          {caseSelected && showDropDowns === true ? (
            <DocumentTypeSelection setDocType={setDocType} />
          ) : null}
          {showVbClaimSelect ? (
            <VBClaimSelect
              claims={availableVbClaims}
              isLoading={vbClaimsLoading}
              selectedClaims={selectedVbClaims}
              setSelectedClaims={setSelectedVbClaims}
            />
          ) : null}
          {docTypeSelected ? (
            <FileSelection
              selectedCase={selectedCaseNew}
              docType={docType}
              hasVbClaims={availableVbClaims.length > 0}
              selectedVbClaimIds={selectedVbClaims.map((c) => c.id)}
              setSuccess={handleSuccess}
              setError={setError}
              setShowDropDowns={setShowDropDowns}
            />
          ) : null}
        </>
      );

      return <Container id={containerId}>{renderUploadForm()}</Container>;
    }
  };

export default NewUploadDocuments;
